import {
  AysClinicUniversityType,
  AYS_CLINIC_UNIVERSITY_TYPE_DISPLAY_NAMES,
} from '@bindoc-projects/clinic-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_VIRTUAL_CLINIC_UNIVERSITY_CONDITION_ID = 'university';
export const FilterConditionClinicUniversityTypePluginType: FilterConditionPluginSpecification<
  AysClinicUniversityType | null,
  FilterConditionClinicUniversityTypeDto,
  BdVirtualClinicUniversityCondition
> = {
  type: 'universityType',
  category: 'clinic',
  displayName: { de: 'Universitätskliniken' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionClinicUniversityTypeDto,
  ): FilterConditionPluginImpl<AysClinicUniversityType | null> {
    return new FilterConditionClinicUniversityTypePlugin(
        obj.universityType as AysClinicUniversityType,
      );
  },
  fromCondition(
    jsonObj: BdVirtualClinicUniversityCondition,
  ): FilterConditionPluginImpl<AysClinicUniversityType | null> {
    return new FilterConditionClinicUniversityTypePlugin(jsonObj.flag);
  },
  newInstance(): FilterConditionPluginImpl<AysClinicUniversityType | null> {
    return new FilterConditionClinicUniversityTypePlugin(
        AysClinicUniversityType.WITH_UNIVERSITIES,
      );
  },
};

export class FilterConditionClinicUniversityTypePlugin extends FilterConditionPluginImpl<
  AysClinicUniversityType | null,
  FilterConditionClinicUniversityTypeDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    AysClinicUniversityType | null,
    FilterConditionClinicUniversityTypeDto,
    BdVirtualClinicUniversityCondition
  > = FilterConditionClinicUniversityTypePluginType;

  constructor(private universityType: AysClinicUniversityType | null) {
    super(FilterConditionClinicUniversityTypePluginType.type);
  }

  override equals(other: FilterConditionClinicUniversityTypePlugin): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this.universityType, other.universityType)
    );
  }

  override validationToText(): string {
    if (this.universityType) {
      return '';
    } else {
      return 'Art der Universitätskliniken auswählen';
    }
  }

  clone(): FilterConditionClinicUniversityTypePlugin {
    return new FilterConditionClinicUniversityTypePlugin(this.universityType);
  }

  toCondition(): BdCondition | null {
    return new BdVirtualClinicUniversityCondition(this.universityType);
  }

  toJSON(): FilterConditionClinicUniversityTypeDto | null {
    return {
      type: this.type,
      universityType: this.universityType,
    };
  }

  toText(): string | null {
    return 'Universitätskliniken';
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    if (!this.universityType) return { title, items: [] };
    const displayName =
      AYS_CLINIC_UNIVERSITY_TYPE_DISPLAY_NAMES[this.universityType] ??
      this.universityType;
    return { title, items: [displayName] };
  }

  toValue(): AysClinicUniversityType | null {
    return this.universityType;
  }
}

export class BdVirtualClinicUniversityCondition extends BdCondition {
  constructor(public flag: AysClinicUniversityType) {
    super(BD_VIRTUAL_CLINIC_UNIVERSITY_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        flag: this.flag,
      },
    };
  }
}

interface FilterConditionClinicUniversityTypeDto {
  type: string;
  universityType: string;
}
